import {Component, inject, input, output, signal} from '@angular/core';
import {filter, of, tap} from 'rxjs';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HttpEventType} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {
  BanIcon,
  CheckIcon,
  CornerUpLeftIcon,
  CornerUpRightIcon,
  ImagePlusIcon,
  LucideAngularModule
} from 'lucide-angular';
import {FileService} from '@core/service/file.service';
import {PictureUtil} from '@shared/util/picture.util';
import {MediaDTO} from '@domain/media/media.dto';
import {NotificationService} from '@shared/notification/notification.service';
import {FieldBaseComponent} from '@shared/form/field-base.component';
import {CoverViewerComponent} from '@shared/form/media/cover-uploader/cover-viewer/cover-viewer.component';
import {FieldContainerComponent} from '@shared/form/field-container/field-container.component';

@Component({
  selector: 'app-cover-uploader',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    LucideAngularModule,
    CoverViewerComponent,
    FieldContainerComponent
  ],
  templateUrl: './cover-uploader.component.html',
  styleUrl: './cover-uploader.component.scss',
})
export class CoverUploaderComponent extends FieldBaseComponent<MediaDTO | null> {

  public readonly THUMBNAIL_MEDIA_QUERY = `(width > ${PictureUtil.THUMBNAIL_MAX_WIDTH}px)`;

  public readonly thumbnailUrl = input.required<string>();
  public readonly url = input.required<string>();
  public readonly uploadLabel = input<string>('Upload');
  public readonly deleteLabel = input<string>('Delete');
  public readonly pictureUploaded = output<MediaDTO | null>();
  public readonly supportedFormats = ['jpg', 'png'];
  public readonly acceptMimeTypes = ['image/jpeg', 'image/png'];

  public readonly imageLoaded = signal(false);

  public readonly MAX_WIDTH = PictureUtil.WEB_MAX_WIDTH;

  public previewUrl: string | null = null;
  private resizedUrl: string | null = null;
  private rotation = 0;
  private originalFile: File | null = null;
  private processedFile: File | null = null;

  public uploadProgress: number | null = null;
  private readonly notifyService = inject(NotificationService);
  private readonly fileService = inject(FileService);

  public readonly icons = {
    Cancel: BanIcon,
    Upload: CheckIcon,
    Left: CornerUpLeftIcon,
    Right: CornerUpRightIcon,
    Image: ImagePlusIcon
  };

  // public mediaId = model<string | null>(null);

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    this.originalFile = file;
    this.resize();
  }

  public onCancelSelect(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  public rotate(direction: 'left' | 'right'): void {
    if (!this.resizedUrl) return;

    this.rotation += direction === 'right' ? 90 : -90;
    this.applyRotation();
  }

  private resize(): void {
    if (!this.originalFile) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // scale down while keeping aspect ratio
        let width = img.width;
        let height = img.height;
        const widthRatio = PictureUtil.WEB_MAX_WIDTH / width;
        const heightRatio = PictureUtil.WEB_MAX_HEIGHT / height;
        const ratio = Math.min(widthRatio, heightRatio, 1);

        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        this.resizedUrl = canvas.toDataURL('image/png');
        this.previewUrl = this.resizedUrl;
        this.rotation = 0;

        // create resized File
        canvas.toBlob((blob) => {
          if (blob) {
            this.processedFile = new File([blob], 'resized.jpg', { type: 'image/jpeg' });
          }
        }, 'image/jpeg', 0.9);
      };
    };
    reader.readAsDataURL(this.originalFile);
  }

  private applyRotation(): void {
    const img = new Image();
    img.src = this.resizedUrl as string;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      const angle = (this.rotation % 360 + 360) % 360;

      // adjust canvas size based on rotation
      if (angle === 90 || angle === 270) {
        canvas.width = img.height;
        canvas.height = img.width;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      this.previewUrl = canvas.toDataURL('image/png');

      canvas.toBlob((blob) => {
        if (blob) {
          this.processedFile = new File([blob], 'rotated.jpg', { type: 'image/jpeg' });
        }
      }, 'image/jpeg', 0.9);
    };
  }

  public delete(): void {
    this.fileService.delete(this.url()).pipe(
      tap(_response => {
        this.setValue(null);
        // this.mediaId.set(null);
        this.reset();
        this.pictureUploaded.emit(null);
      })
    ).subscribe(_ => {});
  }

  public reset(): void {
    this.originalFile = null;
    this.processedFile = null;
    this.rotation = 0;
    this.previewUrl = null;
    this.resizedUrl = null;
    this.imageLoaded.set(false);
  }

  public uploadFile(): void {
    if (!this.processedFile) {
      return;
    }
    const file = this.processedFile;
    this.fileService.upload(this.url(), file).pipe(
      tap(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        }
      }),
      filter(event => event.type === HttpEventType.Response),
      tap(event => {
        const media: MediaDTO | null = event.body ?? null;
        this.pictureUploaded.emit(media);
        this.uploadProgress = null;
        this.setValue(media ?? null);
        // this.mediaId.set(media?.id ?? null);
      }),
      catchError(_err => {
        this.uploadProgress = null;
        this.notifyService.toast({
          message: 'Une erreur est survenue pendant le transfer.'
        });
        return of(undefined);
      })
    ).subscribe(_ => {
      this.reset();
    });
  }

}
